/**
 * Организация текущей сессии — **три состояния, а не два**.
 *
 * Источник — `api.whoami`, поле `company`. С 09.09.2026 (`2dfd989`, шаг 3 T036)
 * оно берётся из `GetEffectiveCompany()` — **той же функции, по которой
 * отбираются данные** (`RegistryBatch`, агрегаты `analysis`, плитки главной).
 * Смысл именно в этом: шапка не должна расходиться с тем, чем отфильтрован
 * список под ней. Прежде поле собиралось соединением с областью сессии, и под
 * районом соединение не находило строки — на одной странице оказывались данные,
 * отобранные по компании, и заголовок «организация не определена».
 *
 * Вид теперь отдаёт **честный `null`**, а не объект со всеми `null` внутри,
 * и `null` этот означает одно: `GetEffectiveCompany()` не разрешилась ни во что.
 * Под новым видом на данных стенда это недостижимо — организация называется во
 * всех одиннадцати областях (замер `dm-db` 09.09), — то есть ветка `COMPANY_NONE`
 * не мёртвая, а спящая: первая же компания без поддерева или несуществующий код
 * в реестре её разбудят.
 *
 * ⚠️ Но **на стенде спящей она станет только после наката**: на 09.09.2026
 * `cloud.debt-master.ru` отвечает прежней редакцией вида (проверено `pg_get_viewdef`
 * и живым `/whoami`), и там эта ветка рабочая — сегодня она единственное, что
 * на стенде видно. База обновляется накатом, а не коммитом. Проверять состояния,
 * которых на стенде нет, — подменой ответа: `wiki/frontend/session-identity.md`.
 *
 * ## Почему проверки остаются, хотя вид стал честным
 *
 * **`company.id`** — потому что честность обеспечена конструкцией вида, а не нашим
 * знанием о ней: `CASE WHEN p.id IS NULL THEN NULL`. Проверка по `.id` верна
 * и при объекте со всеми `null` (так вид отвечал до 09.09 и так он отвечает
 * на стенде, пока туда не накатили правку), и при честном `null`.
 *
 * **Служебная «Все»** (`00000000-0000-4003-a001-000000000000`) — граница здесь
 * сместилась, и это надо знать точно. Раньше «Все» приезжала подменой в самом
 * соединении, выше нас. Теперь она отсекается на стороне базы, но **не целиком**:
 * `nullif` закрывает первое плечо `GetEffectiveCompany()`, явное условие внутри
 * `GetAreaCompany()` — второе, а третье, реестровое умолчание, не закрыто ничем.
 * При `CONFIG\CurrentCompany\Code = 'all'` «Все» дошла бы сюда и напечаталась
 * организацией — и по ней же отбирались бы данные.
 *
 * То есть наша проверка — **не дубль базы, а другая ставка**: база выбирает
 * «пусть шапка совпадает с выборкой, даже если это «Все»», мы выбираем «слово
 * «Все» на месте названия организации — не ответ пользователю». В той
 * конфигурации это стоило бы расхождения шапки со списком — то есть в одной
 * конфигурации проверка защищает, а в другой воспроизводит ровно ту болезнь,
 * ради которой правили вид. Оставлена именно с этим знанием, а не по чьей-то
 * просьбе (согласовано с `dm-db` 09.09.2026): если реестр когда-нибудь укажет
 * на «Все», решать надо будет заново, а не молча.
 *
 * ## Третье состояние
 *
 * «Организации нет» и **«сессия ещё не получена»** — разные ответы, и второй
 * до входа отвечать «не определена» было бы неправдой. Раньше это решалось
 * у вызывающего, и один из двух вызывающих про него забыл, — поэтому состояние
 * вернулось сюда, в единственное место, где вопрос решается.
 *
 * ## Чего здесь чинить нельзя
 *
 * `name` пользователя честного `null` не получит никогда: `reducers/User.js:23`
 * разыменовывает `raw.name.first` без проверки, и честный `null` уронил бы вход
 * всем сразу. Это решение, а не недоделка, — записано и в теле самого вида.
 *
 * @param {object} user — срез `state.user`
 * @returns {{status: string, company: ?object, title: ?string}}
 */
const ALL_COMPANY_ID = "00000000-0000-4003-a001-000000000000";

export const COMPANY_UNKNOWN = "unknown"; // сессии ещё нет — сказать нечего
export const COMPANY_NONE = "none"; // сессия есть, организация не разрешилась
export const COMPANY_SET = "set";

export default function sessionCompany(user) {
  if (!user || !user.isAuthorized) {
    return { status: COMPANY_UNKNOWN, company: null, title: null };
  }

  const company = (user.raw && user.raw.company) || null;
  if (!company || !company.id || company.id === ALL_COMPANY_ID) {
    return { status: COMPANY_NONE, company: null, title: null };
  }

  // `name` — это `pot.label` из `db.object_text` по текущей локали, и это
  // соединение осталось левым намеренно: у существующей организации без строки
  // на локали объект придёт не-null, а имя внутри — пустым. Пустота на месте
  // названия неотличима от «поле не заполнено», поэтому запасным именем идёт
  // `code` — он `NOT NULL` в `db.company` (`company/table.sql:14`).
  return { status: COMPANY_SET, company, title: company.name || company.code };
}
